import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { NettStatusDetailPopup } from '../components/NettStatusDetailPopup';
import EnhancedCreateWODialog from '../components/EnhancedCreateWODialog';
import IndentCreationDialog from '../components/IndentCreationDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package, Factory, Truck, Clock, AlertTriangle, Warehouse,
  RefreshCw, Search, ArrowLeft, BarChart3, FileText, ShoppingCart,
  ExternalLink, ChevronRight, CheckCircle, AlertCircle, CircleDashed
} from 'lucide-react';
import { formatCategory } from '../lib/utils';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch { return '-'; }
};

const COUNTER_CARDS = [
  { key: 'stock_in_hand', label: 'Stock in Hand', color: 'text-slate-800', type: 'stock_in_hand', desc: 'Breakdown of available stock' },
  { key: 'ohwip', label: 'OHWIP', color: 'text-yellow-600', type: 'ohwip', desc: 'Items to be produced in Work Orders' },
  { key: 'wip', label: 'WIP (Comp)', color: 'text-purple-600', type: 'wip', desc: 'Items in active Job Cards' },
  { key: 'wip_rmt', label: 'WIP (RMT)', color: 'text-orange-500', type: 'wip_rmt', desc: 'Raw materials in active Job Cards' },
  { key: 'blocked', label: 'Blocked', color: 'text-orange-600', type: 'blocked', desc: 'Reserved for pending Work Orders' },
  { key: 'so_blocked', label: 'SO Blocked', color: 'text-red-600', type: 'so_blocked', desc: 'Reserved for Buy-type Sales Orders' },
  { key: 'indent_qty', label: 'Indent Qty', color: 'text-teal-600', type: 'indent_qty', desc: 'Purchase requested via Indents' },
  { key: 'po_issued', label: 'PO Issued', color: 'text-green-600', type: 'po_issued', desc: 'Pending Purchase Orders' },
];

// ============================================================================
// FULFILLMENT DASHBOARD — Action-oriented view of items with negative Nett Status
// Uses ONLY /api/fulfillment-dashboard/items + /api/items/{id}/nett-status
// 
// Modes: 'ip' (Production only), 'mp' (Purchase only), 'both' (combined tabs)
// ============================================================================

export function FulfillmentContent({ mode = 'both' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(mode === 'mp' ? 'mp' : 'ip');

  const [selectedItem, setSelectedItem] = useState(null);
  const [nettStatusData, setNettStatusData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPopup, setDetailPopup] = useState(null);

  const [selectedRows, setSelectedRows] = useState(new Set());

  const [woDialogOpen, setWoDialogOpen] = useState(false);
  const [woDefaultQty, setWoDefaultQty] = useState(0);

  const [indentDialogOpen, setIndentDialogOpen] = useState(false);
  const [indentDialogItems, setIndentDialogItems] = useState([]);

  // Page title based on mode
  const pageTitle = mode === 'ip' ? 'Production Fulfillment' 
                  : mode === 'mp' ? 'Purchase Fulfillment' 
                  : 'Fulfillment Dashboard';
  
  const pageIcon = mode === 'ip' ? Factory : mode === 'mp' ? ShoppingCart : BarChart3;
  const PageIcon = pageIcon;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // ===== Data Fetching =====

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fulfillment-dashboard/items`, { headers: getAuthHeader() });
      setItems(res.data.items || []);
    } catch {
      toast.error('Failed to load dashboard items');
    } finally {
      setLoading(false);
    }
  };

  const fetchNettStatus = async (item) => {
    setSelectedItem(item);
    setDetailLoading(true);
    setNettStatusData(null);
    setSelectedRows(new Set());
    try {
      const res = await axios.get(`${API_URL}/api/items/${item.id}/nett-status`, { headers: getAuthHeader() });
      setNettStatusData(res.data);
    } catch {
      toast.error('Failed to load item details');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshNettStatus = async () => {
    if (!selectedItem) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/items/${selectedItem.id}/nett-status`, { headers: getAuthHeader() });
      setNettStatusData(res.data);
      setSelectedRows(new Set());
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setDetailLoading(false);
    }
  };

  // ===== Computed =====

  const { ipItems, mpItems } = useMemo(() => {
    const ip = [], mp = [];
    const term = searchTerm.toLowerCase().trim();
    for (const item of items) {
      if (term && !(item.name || '').toLowerCase().includes(term) && !(item.item_code || '').toLowerCase().includes(term)) continue;
      (item.source_type === 'IP' ? ip : mp).push(item);
    }
    const sortFn = (a, b) => {
      const da = a.earliest_delivery_date || '9999', db = b.earliest_delivery_date || '9999';
      if (da !== db) return da.localeCompare(db);
      if (a.category === 'SFG' && b.category !== 'SFG') return -1;
      if (b.category === 'SFG' && a.category !== 'SFG') return 1;
      return 0;
    };
    ip.sort(sortFn);
    mp.sort(sortFn);
    return { ipItems: ip, mpItems: mp };
  }, [items, searchTerm]);

  const isIP = selectedItem?.source_type === 'IP';
  const itemCategory = selectedItem?.category || nettStatusData?.item?.category || '';
  const isFNG = itemCategory === 'FNG';
  const isSFG = itemCategory === 'SFG';

  // Demand data source:
  //   FNG  → only so_blocked_details (direct customer SOs)
  //   SFG  → merged so_blocked_details (direct SO) + blocked_details (child-of-WO)
  //   else → only blocked_details (WO-BOM consumption — RMT/CTM path)
  // Rows are normalized to a unified shape with a source_kind discriminator so
  // the table, row-action helpers, and bulk selection logic all read from one
  // list regardless of kind.
  const demandSources = useMemo(() => {
    if (!nettStatusData) return [];
    const mapSo = (r) => ({
      source_kind: 'so',
      ref_number: r.so_number || '',
      ref_label: r.client_name || '',
      required_qty: r.quantity || 0,
      fulfilled_qty: (r.dispatched_quantity || 0) + (r.wo_qty_issued || 0) + (r.stock_fulfill_qty || 0),
      pending_qty: r.pending_wo_qty || 0,
      delivery_date: r.delivery_date || null,
      status: r.so_status || r.status || '',
      raw: r,
    });
    const mapWoBom = (r) => ({
      source_kind: 'wo_bom',
      ref_number: r.work_order_number || '',
      ref_label: r.parent_item_name || '',
      required_qty: r.required_qty || 0,
      fulfilled_qty: r.issued_qty || 0,
      pending_qty: r.pending_qty || 0,
      delivery_date: r.delivery_date || null,
      status: r.wo_status || '',
      raw: r,
    });
    const soRaw = nettStatusData.so_blocked_details || [];
    const woRaw = nettStatusData.blocked_details || [];
    let unified = [];
    if (isFNG) {
      unified = soRaw.filter(r => (r.pending_quantity || 0) > 0).map(mapSo);
    } else if (isSFG) {
      unified = [
        ...soRaw.filter(r => (r.pending_quantity || 0) > 0).map(mapSo),
        ...woRaw.filter(r => (r.pending_qty || 0) > 0).map(mapWoBom),
      ];
    } else {
      unified = woRaw.filter(r => (r.pending_qty || 0) > 0).map(mapWoBom);
    }
    return unified.sort((a, b) =>
      String(a.delivery_date || '9999').localeCompare(String(b.delivery_date || '9999'))
    );
  }, [nettStatusData, isFNG, isSFG]);

  // When selection contains rows of one kind, block the opposite kind's
  // checkboxes so bulk Create-WO dispatches cleanly to a single backend path.
  const selectionKind = useMemo(() => {
    if (selectedRows.size === 0) return null;
    for (const i of selectedRows) {
      const r = demandSources[i];
      if (r) return r.source_kind;
    }
    return null;
  }, [selectedRows, demandSources]);

  // Check if row needs a WO — used for checkbox visibility / action enablement.
  const rowNeedsWO = (row) => {
    if (!row) return false;
    // 'so' rows: need WO when the SO has un-allocated qty
    if (row.source_kind === 'so') return (row.raw?.pending_wo_qty || 0) > 0;
    // 'wo_bom' rows: always require production (parent WO is waiting on this material)
    return (row.pending_qty || 0) > 0;
  };

  // Check if row can have WO created (BOM ready)
  // Note: WOs CAN be created even when variants are pending — variant declaration updates WO later
  const rowCanCreateWO = (row) => {
    if (!nettStatusData) return false;
    // BOM must be ready
    if (nettStatusData.bom_status === 'pending') return false;
    // Must have pending WO qty
    return rowNeedsWO(row);
  };

  // Normalized getters — work on the unified row shape regardless of kind.
  const getRowWOQty = (row) => (row?.pending_qty || 0);
  const getRowPendingQty = (row) => (row?.pending_qty || 0);

  // Extract comma-joined SO numbers associated with a row (for indent payload).
  const getRowSoNumbers = (row) => {
    if (!row) return [];
    if (row.source_kind === 'so') return row.ref_number ? [row.ref_number] : [];
    const s = row.raw?.so_number || '';
    return s.split(',').map(x => x.trim()).filter(Boolean);
  };

  // Get delivery date urgency class
  const getDeliveryDateClass = (dateStr) => {
    if (!dateStr) return 'text-muted-foreground';
    try {
      const delivery = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysRemaining = Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));
      if (daysRemaining <= 0) return 'text-red-600 font-semibold'; // Overdue
      if (daysRemaining <= 3) return 'text-red-500 font-medium'; // Critical
      if (daysRemaining <= 7) return 'text-amber-600'; // Warning
      return 'text-green-600'; // Safe
    } catch {
      return 'text-muted-foreground';
    }
  };

  // Format WO allocations for display
  const formatWOAllocations = (allocations) => {
    if (!allocations || allocations.length === 0) return null;
    return allocations.map(a => ({
      number: a.work_order_number,
      qty: a.quantity,
      status: a.status
    }));
  };

  // Get WO status display text
  const getWOStatusText = (status) => {
    switch (status) {
      case 'created': return 'Created';
      case 'issued': return 'In Progress';
      case 'completed': case 'received': return 'Completed';
      default: return status || 'Unknown';
    }
  };

  // ===== Helpers =====

  const buildAvailableSOs = () => {
    if (!nettStatusData) return [];
    const sos = new Map();
    for (const so of nettStatusData.so_blocked_details || []) {
      if (so.pending_quantity > 0 && so.sales_order_id)
        sos.set(so.so_number, { id: so.sales_order_id, number: so.so_number });
    }
    for (const row of nettStatusData.blocked_details || []) {
      if (row.so_number) {
        for (const n of row.so_number.split(',').map(s => s.trim()).filter(Boolean)) {
          if (!sos.has(n)) sos.set(n, { id: n, number: n });
        }
      }
    }
    return Array.from(sos.values());
  };

  const buildIndentPayload = (qty, soNumbers = []) => {
    const d = nettStatusData;
    if (!d) return [];
    const availSOs = buildAvailableSOs();
    const defSO = soNumbers.length === 1 ? availSOs.find(s => s.number === soNumbers[0]) : null;
    return [{
      item_id: selectedItem.id,
      item_name: d.item.name,
      item_code: d.item.code,
      unit: d.item.unit || 'NOS',
      source_type: 'MP',
      nett_status: d.stock_position?.current?.nett_status || 0,
      default_qty: qty,
      available_sos: availSOs,
      default_so_id: defSO?.id || '',
      default_so_number: defSO?.number || ''
    }];
  };

  // ===== Actions =====

  const backToList = () => {
    setSelectedItem(null);
    setNettStatusData(null);
    setDetailPopup(null);
    setSelectedRows(new Set());
    fetchItems();
  };

  const toggleRow = (idx) => {
    const row = demandSources[idx];
    if (!rowCanCreateWO(row)) return; // Don't toggle if can't create WO
    // Block mixed-kind selection. If a selection already exists of the opposite
    // kind, silently ignore — the UI also disables the checkbox visually.
    if (selectionKind && row.source_kind !== selectionKind) return;
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    // Only toggle rows that can create WO AND match the current selection kind.
    // If no current selection, default to the first eligible row's kind.
    const candidates = demandSources.filter(r => rowCanCreateWO(r));
    const kind = selectionKind || candidates[0]?.source_kind || null;
    const eligibleIndices = demandSources
      .map((r, i) => (rowCanCreateWO(r) && r.source_kind === kind) ? i : -1)
      .filter(i => i >= 0);

    setSelectedRows(prev => {
      const allSelected = eligibleIndices.length > 0 && eligibleIndices.every(i => prev.has(i));
      return allSelected ? new Set() : new Set(eligibleIndices);
    });
  };

  // IP: Create WO actions (action based on source_type, qty from pending_wo_qty for FNG)
  const openWOForRow = (row) => {
    setWoDefaultQty(getRowWOQty(row));
    setWoDialogOpen(true);
  };
  const openWOForSelected = () => {
    // Only sum rows that can create WO
    const total = [...selectedRows].reduce((s, i) => {
      const row = demandSources[i];
      return rowNeedsWO(row) ? s + getRowWOQty(row) : s;
    }, 0);
    setWoDefaultQty(total);
    setWoDialogOpen(true);
  };
  const openWOForAll = () => {
    // Sum pending_qty across all unified rows that need a WO.
    const total = demandSources.reduce((s, row) =>
      rowNeedsWO(row) ? s + getRowWOQty(row) : s, 0);
    setWoDefaultQty(total);
    setWoDialogOpen(true);
  };

  // MP: Create Indent actions (action based on source_type, qty from category-appropriate field)
  const openIndentForRow = (row) => {
    setIndentDialogItems(buildIndentPayload(getRowPendingQty(row), getRowSoNumbers(row)));
    setIndentDialogOpen(true);
  };
  const openIndentForSelected = () => {
    const total = [...selectedRows].reduce((s, i) => s + getRowPendingQty(demandSources[i]), 0);
    const allSOs = [...new Set([...selectedRows].flatMap(i => getRowSoNumbers(demandSources[i])))];
    setIndentDialogItems(buildIndentPayload(total, allSOs));
    setIndentDialogOpen(true);
  };
  const openIndentForAll = () => {
    const allSOs = [...new Set((nettStatusData?.blocked_details || []).flatMap(r =>
      r.so_number ? r.so_number.split(',').map(s => s.trim()).filter(Boolean) : []
    ))];
    setIndentDialogItems(buildIndentPayload(
      Math.abs(nettStatusData?.stock_position?.current?.nett_status || 0), allSOs
    ));
    setIndentDialogOpen(true);
  };

  const data = nettStatusData;
  const stock = data?.stock_position;

  // Get items based on mode
  const displayItems = mode === 'ip' ? ipItems : mode === 'mp' ? mpItems : null;

  // ===== LIST VIEW =====
  if (!selectedItem) {
    return (
        <div className="space-y-3" data-testid="fulfillment-dashboard">
          <Card>
            <CardContent className="py-3">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2 mr-4">
                  <PageIcon className="w-5 h-5 text-primary" />
                  <h1 className="text-lg font-bold">{pageTitle}</h1>
                  <Badge variant="destructive" className="text-xs">Deficit Items Only</Badge>
                  {mode !== 'both' && (
                    <Badge variant="outline" className="text-xs">
                      {displayItems?.length || 0} items
                    </Badge>
                  )}
                </div>
                <div className="flex-1 min-w-[180px] max-w-[300px]">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search by name or code..." value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-8 text-sm"
                      data-testid="dashboard-search" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Single mode: show table directly without tabs */}
          {mode !== 'both' ? (
            <ItemListTable items={displayItems} loading={loading} onItemClick={fetchNettStatus} />
          ) : (
            /* Combined mode: show tabs */
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 w-full max-w-md">
                <TabsTrigger value="ip" data-testid="tab-ip">
                  IP — Internal Production ({ipItems.length})
                </TabsTrigger>
                <TabsTrigger value="mp" data-testid="tab-mp">
                  MP — Market Purchase ({mpItems.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="ip">
                <ItemListTable items={ipItems} loading={loading} onItemClick={fetchNettStatus} />
              </TabsContent>
              <TabsContent value="mp">
                <ItemListTable items={mpItems} loading={loading} onItemClick={fetchNettStatus} />
              </TabsContent>
            </Tabs>
          )}
        </div>
    );
  }

  // ===== EXPANDED VIEW =====
  return (
      <div className="space-y-3" data-testid="fulfillment-detail-view">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={backToList} data-testid="back-to-list-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate" data-testid="detail-item-code">
              {data?.item?.code || selectedItem.item_code}
            </h1>
            <span className="text-sm text-muted-foreground truncate">
              {data?.item?.name || selectedItem.name}
            </span>
            {data?.item?.category && (
              <Badge variant="outline">{formatCategory(data.item.category)}</Badge>
            )}
            <Badge variant={isIP ? 'default' : 'secondary'} className="text-xs">
              {selectedItem.source_type}
            </Badge>
            {/* BOM Status Badge - only show for IP items */}
            {isIP && nettStatusData && (
              nettStatusData.bom_status === 'pending' ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="destructive" className="text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> BOM Pending
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>BOM must be created before Work Order can be created</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Badge variant="outline" className="text-xs flex items-center gap-1 border-green-500 text-green-700 bg-green-50">
                  <CheckCircle className="w-3 h-3" /> BOM ✓
                </Badge>
              )
            )}
          </div>
          <div className="text-right shrink-0">
            <span className="text-xs text-muted-foreground block">Nett Status</span>
            <span
              className={`text-2xl font-bold ${(stock?.current?.nett_status || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}
              data-testid="detail-nett-status"
            >
              {stock?.current?.nett_status != null ? Number(stock.current.nett_status).toFixed(2) : '...'}
            </span>
          </div>
        </div>

        {detailLoading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading...</span>
          </div>
        )}

        {data && !detailLoading && (
          <>
            {/* Counter Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              {COUNTER_CARDS.map(card => (
                <Card
                  key={card.key}
                  className="cursor-pointer hover:shadow-md transition-all"
                  onClick={() => setDetailPopup({
                    type: card.type,
                    title: card.label,
                    description: card.desc
                  })}
                  data-testid={`card-${card.key}`}
                >
                  <CardContent className="pt-2 pb-2 px-3">
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      {card.label} <ExternalLink className="w-2.5 h-2.5" />
                    </p>
                    <p className={`text-lg font-bold ${card.color}`}>
                      {stock?.current?.[card.key] ?? 0}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Demand Sources */}
            <Card>
              <CardContent className="p-0">
                <div className="p-3 border-b flex items-center justify-between bg-slate-50">
                  <h3 className="font-semibold text-sm" data-testid="demand-sources-title">
                    {isFNG
                      ? 'Demand Sources — Sales Orders'
                      : isSFG
                      ? 'Demand Sources'
                      : 'Demand Sources — Work Order Requirements'}
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {demandSources.length} source(s) — sorted by delivery date
                  </span>
                </div>

                {/* Multi-select action bar */}
                {selectedRows.size > 0 && (
                  <div className="px-3 py-2 bg-primary/5 border-b flex items-center justify-between"
                    data-testid="multi-select-bar">
                    <span className="text-sm font-medium">{selectedRows.size} row(s) selected</span>
                    <Button size="sm" onClick={isIP ? openWOForSelected : openIndentForSelected}
                      data-testid="multi-select-action-btn">
                      {isIP
                        ? <><Factory className="w-3 h-3 mr-1" />Create WO for Selected ({selectedRows.size})</>
                        : <><FileText className="w-3 h-3 mr-1" />Create Indent for Selected ({selectedRows.size})</>
                      }
                    </Button>
                  </div>
                )}

                {demandSources.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">
                    No active demand sources
                  </div>
                ) : (
                  <div className="overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 text-[11px]">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={(() => {
                                const kind = selectionKind || demandSources.find(r => rowCanCreateWO(r))?.source_kind || null;
                                const eligible = kind ? demandSources.filter(r => rowCanCreateWO(r) && r.source_kind === kind) : [];
                                return eligible.length > 0 && eligible.every((_, i) => selectedRows.has(demandSources.indexOf(eligible[i])));
                              })()}
                              onCheckedChange={toggleAll}
                              data-testid="select-all-checkbox"
                            />
                          </TableHead>
                          <TableHead className="w-14">Kind</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Detail</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-right">Required</TableHead>
                          <TableHead className="text-center">Coverage / Issued</TableHead>
                          <TableHead className="text-right">Pending</TableHead>
                          <TableHead>Delivery</TableHead>
                          <TableHead className="w-28 text-center">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {demandSources.map((row, idx) => {
                          const needsWO = rowNeedsWO(row);
                          const canCreate = rowCanCreateWO(row);
                          const kindMismatch = selectionKind && row.source_kind !== selectionKind;
                          const isSoRow = row.source_kind === 'so';
                          const rawRow = row.raw || {};
                          const woAllocs = isSoRow ? formatWOAllocations(rawRow.wo_allocations) : null;
                          const isVariantPending = isSoRow && rawRow.so_status === 'pending_variant_selection';
                          const isCovered = isSoRow && !needsWO && woAllocs && woAllocs.length > 0;

                          return (
                          <TableRow key={idx} className={`text-xs ${isCovered ? 'bg-green-50/50' : ''}`} data-testid={`demand-row-${idx}`}>
                            <TableCell>
                              {canCreate ? (
                                <Checkbox
                                  checked={selectedRows.has(idx)}
                                  disabled={kindMismatch}
                                  onCheckedChange={() => toggleRow(idx)}
                                  data-testid={`row-checkbox-${idx}`}
                                />
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {isSoRow ? (
                                <Badge variant="outline" className="text-[10px] border-blue-400 text-blue-700 bg-blue-50" data-testid={`row-kind-${idx}`}>
                                  SO
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50" data-testid={`row-kind-${idx}`}>
                                  WO
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="font-mono font-medium">
                              {row.ref_number || '-'}
                            </TableCell>
                            <TableCell className="max-w-[160px] truncate" title={row.ref_label}>
                              {row.ref_label || '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              {isSoRow ? (
                                isVariantPending ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50">
                                          Variant Pending
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Variant selection required before WO can be created</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <Badge variant="outline" className="text-[10px]">
                                    {row.status === 'confirmed' ? 'Confirmed' : row.status || '-'}
                                  </Badge>
                                )
                              ) : (
                                <Badge variant="outline" className="text-[10px]">
                                  {getWOStatusText(row.status)}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{row.required_qty || 0}</TableCell>
                            <TableCell className="text-center">
                              {isSoRow ? (
                                woAllocs && woAllocs.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    {woAllocs.map((wo, i) => (
                                      <TooltipProvider key={i}>
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <Badge
                                              variant="outline"
                                              className={`text-[10px] ${
                                                needsWO
                                                  ? 'border-amber-400 text-amber-700 bg-amber-50'
                                                  : 'border-green-500 text-green-700 bg-green-50'
                                              }`}
                                            >
                                              {wo.number}: {wo.qty}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{wo.number} ({getWOStatusText(wo.status)}): {wo.qty} pcs allocated</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ))}
                                  </div>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                    <CircleDashed className="w-3 h-3 mr-1" /> No WO
                                  </Badge>
                                )
                              ) : (
                                <span className="text-[11px] text-muted-foreground">Issued: {row.fulfilled_qty || 0}</span>
                              )}
                            </TableCell>
                            <TableCell className={`text-right font-bold ${needsWO ? (isSoRow ? 'text-red-600' : 'text-orange-600') : 'text-green-600'}`}>
                              {needsWO ? (row.pending_qty || 0) : (
                                <span className="flex items-center justify-end gap-1">
                                  <CheckCircle className="w-3 h-3" /> 0
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={getDeliveryDateClass(row.delivery_date)}>
                              {formatDate(row.delivery_date)}
                            </TableCell>
                            <TableCell className="text-center">
                              {isCovered ? (
                                <Badge variant="outline" className="text-[10px] border-green-500 text-green-700 bg-green-50">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Covered
                                </Badge>
                              ) : isIP ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 text-[11px] px-2"
                                          onClick={() => openWOForRow(row)}
                                          disabled={!canCreate}
                                          data-testid={`create-wo-row-${idx}`}
                                        >
                                          <Factory className="w-3 h-3 mr-1" /> Create WO
                                        </Button>
                                      </span>
                                    </TooltipTrigger>
                                    {!canCreate && (
                                      <TooltipContent>
                                        <p>
                                          {nettStatusData?.bom_status === 'pending'
                                            ? 'BOM must be created first'
                                            : 'Cannot create WO'}
                                        </p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Button size="sm" variant="outline" className="h-6 text-[11px] px-2"
                                  onClick={() => openIndentForRow(row)}
                                  data-testid={`create-indent-row-${idx}`}>
                                  <FileText className="w-3 h-3 mr-1" /> Indent
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Create All */}
                {demandSources.length > 0 && demandSources.some(r => rowNeedsWO(r)) && (
                  <div className="p-3 border-t flex justify-end" data-testid="create-all-section">
                    {nettStatusData?.bom_status === 'pending' && isIP ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button disabled data-testid="create-all-btn">
                                <Factory className="w-4 h-4 mr-2" />Create WO for All
                                <span className="ml-1 text-xs opacity-80">
                                  ({demandSources.reduce((s, r) => s + getRowWOQty(r), 0)} qty)
                                </span>
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>BOM must be created first</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Button onClick={isIP ? openWOForAll : openIndentForAll}
                        data-testid="create-all-btn">
                        {isIP
                          ? <><Factory className="w-4 h-4 mr-2" />Create WO for All</>
                          : <><FileText className="w-4 h-4 mr-2" />Create Indent for All</>
                        }
                        <span className="ml-1 text-xs opacity-80">
                          ({demandSources.reduce((s, r) => s + (rowNeedsWO(r) ? getRowWOQty(r) : 0), 0)} qty)
                        </span>
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Shared Popups & Dialogs */}
        <NettStatusDetailPopup
          detailPopup={detailPopup}
          nettStatusData={nettStatusData}
          stock={stock}
          onClose={() => setDetailPopup(null)}
        />

        <EnhancedCreateWODialog
          open={woDialogOpen}
          onOpenChange={setWoDialogOpen}
          itemId={selectedItem?.id}
          itemName={data?.item?.name || selectedItem?.name}
          itemCode={data?.item?.code || selectedItem?.item_code}
          defaultQty={woDefaultQty}
          nettStatus={stock?.current}
          onSuccess={refreshNettStatus}
        >
          {selectedItem && nettStatusData && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Category:</span>
                  <span className="ml-2 font-medium">{formatCategory(selectedItem.category || nettStatusData.item?.category || '')}</span>
                </div>
                <div>
                  <span className="text-slate-500">Total Demand:</span>
                  <span className="ml-2 font-medium">
                    {demandSources.reduce((sum, r) => sum + (r.pending_qty || 0), 0).toLocaleString('en-IN')} pcs
                  </span>
                </div>
              </div>
            </div>
          )}
        </EnhancedCreateWODialog>

        <IndentCreationDialog
          open={indentDialogOpen}
          onOpenChange={setIndentDialogOpen}
          items={indentDialogItems}
          onSuccess={refreshNettStatus}
          title="Create Indent"
          description="Create purchase requisition for this material."
          sourceContext="fulfillment_dashboard"
        />
      </div>
  );
}

// Default export: Combined view with DashboardLayout wrapper
export default function FulfillmentDashboardPage() {
  return (
    <DashboardLayout>
      <FulfillmentContent mode="both" />
    </DashboardLayout>
  );
}

// ============================================================================
// ItemListTable — Simple 6-column list for both IP and MP tabs
// ============================================================================
function ItemListTable({ items, loading, onItemClick }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-primary" />
          <span className="ml-2 text-sm text-muted-foreground">Loading items...</span>
        </CardContent>
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm text-muted-foreground">No items with negative Nett Status</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-auto max-h-[calc(100vh-240px)]">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-100 text-[11px]">
                <TableHead className="min-w-[100px]">Item Code</TableHead>
                <TableHead className="min-w-[180px]">Item Name</TableHead>
                <TableHead className="text-center w-[80px]">BOM</TableHead>
                <TableHead className="text-center w-[70px]">Category</TableHead>
                <TableHead className="text-right w-[90px]">Nett Status</TableHead>
                <TableHead className="w-[120px]">Earliest Delivery</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id}
                  className="cursor-pointer hover:bg-slate-50 transition-colors text-xs"
                  onClick={() => onItemClick(item)}
                  data-testid={`item-row-${item.id}`}>
                  <TableCell className="font-mono font-medium">{item.item_code || '-'}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{item.name}</TableCell>
                  <TableCell className="text-center">
                    {item.source_type === 'IP' && item.bom_status ? (
                      item.bom_status === 'ready' ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-500 text-green-700 bg-green-50">
                                <CheckCircle className="w-3 h-3 mr-0.5" /> BOM ✓
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>BOM is ready for production</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                <AlertCircle className="w-3 h-3 mr-0.5" /> Pending
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>BOM must be created before Work Order</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {formatCategory(item.category)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-bold text-red-600">
                    <span className="flex items-center justify-end gap-0.5">
                      <AlertTriangle className="w-3 h-3" />
                      {(item.nett_status || 0).toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(item.earliest_delivery_date)}
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-slate-50">
          {items.length} deficit items — sorted by earliest delivery date
        </div>
      </CardContent>
    </Card>
  );
}
